(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-finch-collection]';
  const VIEW_BUTTON_SELECTOR = '.fc-view-button[data-view], .fc-view-button[data-collection-view]';
  const COMPARE_TOGGLE_SELECTOR = '.fc-compare-toggle';
  const PRODUCT_COMPARE_SELECTOR = '.fc-product-card__compare';
  const OPEN_COMPARE_SELECTOR = '[data-compare-open], .fc-compare-tray__open';
  const CLOSE_COMPARE_SELECTOR = '[data-compare-close], .fc-compare-dialog__close';
  const CLEAR_COMPARE_SELECTOR = '[data-compare-clear], .fc-compare-tray__clear';
  const READ_MORE_SELECTOR = '[data-collection-read-more]';
  const MAX_COMPARE_PRODUCTS = 3;
  const REGISTRY_KEY = Symbol.for('finch.collection.controllers');

  const registry = window[REGISTRY_KEY] || {
    controllers: new WeakMap(),
    listenersAttached: false,
  };

  window[REGISTRY_KEY] = registry;

  class FinchCollectionController {
    constructor(root) {
      this.root = root;
      this.sectionId =
        root.dataset.sectionId ||
        root.closest('.shopify-section')?.id ||
        root.id ||
        'collection';
      this.storagePrefix = `finchCollection:${this.sectionId}:`;
      this.maxProducts = this.getMaxProducts();
      this.compareAvailable = root.dataset.compareAvailable !== 'false';
      this.view = this.getInitialView();
      this.compareEnabled = this.compareAvailable && this.getStoredBoolean('compareEnabled', false);
      this.selectedProducts = this.compareAvailable ? this.getStoredProducts() : [];
      this.lastDialogFingerprint = '';
      this.dialogOpener = null;
      this.syncScheduled = false;

      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleDialogClose = this.handleDialogClose.bind(this);
      this.handleMutations = this.handleMutations.bind(this);

      root.addEventListener('click', this.handleClick);
      root.addEventListener('change', this.handleChange);
      root.addEventListener('keydown', this.handleKeydown);

      this.observer = new MutationObserver(this.handleMutations);
      this.observer.observe(root, { childList: true, subtree: true });

      this.bindDialogEvents();
      this.syncUI({ renderDialog: true });
    }

    destroy() {
      this.observer?.disconnect();
      this.root.removeEventListener('click', this.handleClick);
      this.root.removeEventListener('change', this.handleChange);
      this.root.removeEventListener('keydown', this.handleKeydown);
      this.getDialog()?.removeEventListener('close', this.handleDialogClose);
      registry.controllers.delete(this.root);
    }

    getMaxProducts() {
      const parsed = Number.parseInt(
        this.root.dataset.compareMax || this.root.dataset.compareLimit,
        10
      );
      return Number.isFinite(parsed) && parsed > 0 ? parsed : MAX_COMPARE_PRODUCTS;
    }

    storageKey(name) {
      return `${this.storagePrefix}${name}`;
    }

    readStorage(name) {
      try {
        return window.sessionStorage.getItem(this.storageKey(name));
      } catch (_error) {
        return null;
      }
    }

    writeStorage(name, value) {
      try {
        window.sessionStorage.setItem(this.storageKey(name), value);
      } catch (_error) {
        // Collection controls remain functional when storage is unavailable.
      }
    }

    getStoredBoolean(name, fallback) {
      const stored = this.readStorage(name);
      if (stored === 'true') return true;
      if (stored === 'false') return false;
      return fallback;
    }

    getInitialView() {
      const stored = this.readStorage('view');
      const initial = stored || this.root.dataset.view || this.root.dataset.defaultView || 'grid';
      return initial === 'list' ? 'list' : 'grid';
    }

    getStoredProducts() {
      const raw = this.readStorage('products');
      if (!raw) return [];

      try {
        const products = JSON.parse(raw);
        if (!Array.isArray(products)) return [];

        return products
          .filter((product) => product && typeof product === 'object' && String(product.id || '').trim())
          .slice(0, this.maxProducts)
          .map((product) => ({
            id: String(product.id),
            title: String(product.title || ''),
            url: String(product.url || ''),
            image: String(product.image || ''),
            price: String(product.price || ''),
            type: String(product.type || ''),
          }));
      } catch (_error) {
        return [];
      }
    }

    persistState() {
      this.writeStorage('view', this.view);
      this.writeStorage('compareEnabled', String(this.compareEnabled));
      this.writeStorage('products', JSON.stringify(this.selectedProducts));
    }

    handleClick(event) {
      const readMoreLink = event.target.closest(READ_MORE_SELECTOR);
      if (readMoreLink && this.root.contains(readMoreLink)) {
        const targetSelector = readMoreLink.getAttribute('href');
        if (targetSelector?.startsWith('#')) {
          const target = document.querySelector(targetSelector);
          const description = target?.matches('finch-collection-description')
            ? target
            : target?.nextElementSibling?.matches('finch-collection-description')
              ? target.nextElementSibling
              : target?.closest('finch-collection-description');
          description?.setExpanded?.(true);
        }
        return;
      }

      const viewButton = event.target.closest(VIEW_BUTTON_SELECTOR);
      if (viewButton && this.root.contains(viewButton)) {
        event.preventDefault();
        this.setView(viewButton.dataset.view || viewButton.dataset.collectionView);
        return;
      }

      const compareToggle = event.target.closest(COMPARE_TOGGLE_SELECTOR);
      if (compareToggle && this.root.contains(compareToggle)) {
        if (!this.isCheckableInput(compareToggle)) {
          event.preventDefault();
          this.setCompareEnabled(!this.compareEnabled);
        }
        return;
      }

      const productToggle = event.target.closest(PRODUCT_COMPARE_SELECTOR);
      if (productToggle && this.root.contains(productToggle)) {
        if (!this.isCheckableInput(productToggle)) {
          event.preventDefault();
          this.toggleProduct(productToggle);
        }
        return;
      }

      const openButton = event.target.closest(OPEN_COMPARE_SELECTOR);
      if (openButton && this.root.contains(openButton)) {
        event.preventDefault();
        this.openDialog(openButton);
        return;
      }

      const closeButton = event.target.closest(CLOSE_COMPARE_SELECTOR);
      if (closeButton && this.root.contains(closeButton)) {
        event.preventDefault();
        this.closeDialog();
        return;
      }

      const clearButton = event.target.closest(CLEAR_COMPARE_SELECTOR);
      if (clearButton && this.root.contains(clearButton)) {
        event.preventDefault();
        this.clearProducts();
        return;
      }

      const dialog = this.getDialog();
      if (dialog && event.target === dialog) {
        this.closeDialog();
      }
    }

    handleChange(event) {
      const compareToggle = event.target.closest(COMPARE_TOGGLE_SELECTOR);
      if (compareToggle && this.root.contains(compareToggle) && this.isCheckableInput(compareToggle)) {
        this.setCompareEnabled(compareToggle.checked);
        return;
      }

      const productToggle = event.target.closest(PRODUCT_COMPARE_SELECTOR);
      if (productToggle && this.root.contains(productToggle) && this.isCheckableInput(productToggle)) {
        this.toggleProduct(productToggle, productToggle.checked);
      }
    }

    handleKeydown(event) {
      if (event.key !== 'Escape') return;

      const dialog = this.getDialog();
      if (dialog && this.isDialogOpen(dialog) && typeof dialog.close !== 'function') {
        event.preventDefault();
        this.closeDialog();
      }
    }

    handleDialogClose() {
      const dialog = this.getDialog();
      dialog?.classList.remove('is-open');
      dialog?.setAttribute('aria-hidden', 'true');

      if (this.dialogOpener?.isConnected) {
        this.dialogOpener.focus({ preventScroll: true });
      }
      this.dialogOpener = null;
    }

    handleMutations(mutations) {
      const hasRelevantMutation = mutations.some((mutation) => {
        const element = mutation.target.nodeType === Node.ELEMENT_NODE ? mutation.target : mutation.target.parentElement;
        return !element?.closest('.fc-compare-tray, .fc-compare-dialog, [data-compare-status]');
      });

      if (!hasRelevantMutation || this.syncScheduled) return;

      this.syncScheduled = true;
      window.requestAnimationFrame(() => {
        this.syncScheduled = false;
        this.bindDialogEvents();
        this.syncUI({ renderDialog: this.isDialogOpen(this.getDialog()) });
      });
    }

    isCheckableInput(element) {
      return (
        element instanceof HTMLInputElement &&
        (element.type === 'checkbox' || element.type === 'radio')
      );
    }

    setView(nextView) {
      if (nextView !== 'grid' && nextView !== 'list') return;
      this.view = nextView;
      this.persistState();
      this.syncViewUI();
    }

    setCompareEnabled(enabled) {
      this.compareEnabled = this.compareAvailable && Boolean(enabled);

      if (!this.compareEnabled && this.root.dataset.compareClearOnDisable === 'true') {
        this.selectedProducts = [];
      }

      this.persistState();
      this.syncCompareUI({ renderDialog: true });
    }

    toggleProduct(control, desiredState) {
      if (!this.compareEnabled) return;

      const product = this.readProduct(control);
      if (!product) return;

      const selectedIndex = this.selectedProducts.findIndex((entry) => entry.id === product.id);
      const shouldSelect = typeof desiredState === 'boolean' ? desiredState : selectedIndex === -1;

      if (shouldSelect && selectedIndex === -1) {
        if (this.selectedProducts.length >= this.maxProducts) {
          this.announceLimit();
          this.syncCompareUI({ renderDialog: false });
          return;
        }
        this.selectedProducts.push(product);
      } else if (shouldSelect && selectedIndex !== -1) {
        this.selectedProducts[selectedIndex] = product;
      } else if (!shouldSelect && selectedIndex !== -1) {
        this.selectedProducts.splice(selectedIndex, 1);
      }

      this.persistState();
      this.syncCompareUI({ renderDialog: true });
    }

    clearProducts() {
      this.selectedProducts = [];
      this.persistState();
      this.syncCompareUI({ renderDialog: true });
      this.closeDialog();
    }

    readProduct(control) {
      const source =
        control.closest('[data-compare-product], [data-product-id], .fc-product-card') || control;
      const link = source.querySelector('.fc-product-card__title a, a.fc-product-card__title, a[href]');
      const image = source.querySelector('.fc-product-card__image, img');
      const titleElement = source.querySelector('.fc-product-card__title');
      const priceElement = source.querySelector('.fc-product-card__price-row, .price');
      const typeElement = source.querySelector('.fc-product-card__category, .fc-product-card__type');
      const data = { ...source.dataset, ...control.dataset };
      const id = String(data.productId || data.compareId || data.id || data.productUrl || link?.href || '').trim();

      if (!id) return null;

      return {
        id,
        title: String(data.productTitle || data.compareTitle || titleElement?.textContent || '').trim(),
        url: String(data.productUrl || data.compareUrl || link?.getAttribute('href') || '').trim(),
        image: String(
          data.productImage ||
            data.compareImage ||
            image?.currentSrc ||
            image?.getAttribute('src') ||
            ''
        ).trim(),
        price: String(data.productPrice || data.comparePrice || priceElement?.textContent || '').trim(),
        type: String(
          data.productType ||
            data.productCategory ||
            data.compareType ||
            typeElement?.textContent ||
            ''
        ).trim(),
      };
    }

    syncUI({ renderDialog = false } = {}) {
      this.syncViewUI();
      this.syncCompareUI({ renderDialog });
    }

    syncViewUI() {
      this.root.dataset.view = this.view;
      this.root.dataset.collectionView = this.view;

      this.root.querySelectorAll(VIEW_BUTTON_SELECTOR).forEach((button) => {
        const isActive = (button.dataset.view || button.dataset.collectionView) === this.view;
        button.setAttribute('aria-pressed', String(isActive));
        button.classList.toggle('is-active', isActive);
      });

      this.root.querySelectorAll('.fc-product-grid').forEach((grid) => {
        grid.classList.toggle('fc-product-grid--list', this.view === 'list');
      });
    }

    syncCompareUI({ renderDialog = false } = {}) {
      const selectedIds = new Set(this.selectedProducts.map((product) => product.id));
      const atLimit = this.selectedProducts.length >= this.maxProducts;

      this.root.dataset.compareEnabled = String(this.compareEnabled);
      this.root.classList.toggle('is-compare-enabled', this.compareEnabled);
      this.root.classList.toggle('is-compare-limit-reached', atLimit);

      this.root.querySelectorAll(COMPARE_TOGGLE_SELECTOR).forEach((toggle) => {
        toggle.setAttribute('role', 'switch');
        toggle.setAttribute('aria-checked', String(this.compareEnabled));
        toggle.classList.toggle('is-active', this.compareEnabled);
        if (this.isCheckableInput(toggle)) toggle.checked = this.compareEnabled;
      });

      this.root.querySelectorAll(PRODUCT_COMPARE_SELECTOR).forEach((control) => {
        const product = this.readProduct(control);
        const isSelected = product ? selectedIds.has(product.id) : false;
        const isDisabled = this.compareEnabled && atLimit && !isSelected;

        control.hidden = !this.compareEnabled;
        control.setAttribute('aria-pressed', String(isSelected));
        control.classList.toggle('is-selected', isSelected);
        control.classList.toggle('is-disabled', isDisabled);

        if (this.isCheckableInput(control)) control.checked = isSelected;
        if ('disabled' in control) control.disabled = false;
      });

      this.updateTray();
      if (renderDialog) this.renderDialogItems();
    }

    updateTray() {
      const tray = this.root.querySelector('.fc-compare-tray');
      if (!tray) return;

      const count = this.selectedProducts.length;
      tray.hidden = !this.compareEnabled || count === 0;
      tray.dataset.count = String(count);

      tray.querySelectorAll('[data-compare-count], .fc-compare-tray__count').forEach((element) => {
        const valueElement = element.querySelector('[data-compare-count-value]');
        if (valueElement) {
          valueElement.textContent = String(count);
          return;
        }

        const singular = element.dataset.countSingular || tray.dataset.countSingular;
        const plural = element.dataset.countPlural || tray.dataset.countPlural;
        const template = (count === 1 ? singular : plural) || element.dataset.countTemplate || tray.dataset.countTemplate;
        element.textContent = template
          ? this.interpolate(template, { count, max: this.maxProducts })
          : `${count} / ${this.maxProducts}`;
      });

      tray.querySelectorAll(OPEN_COMPARE_SELECTOR).forEach((button) => {
        button.disabled = count === 0;
      });
    }

    announceLimit() {
      const status = this.root.querySelector('[data-compare-status]');
      if (!status) return;

      const message =
        status.dataset.limitMessage ||
        this.root.dataset.compareLimitMessage ||
        status.dataset.message ||
        '';
      status.textContent = this.interpolate(message, {
        count: this.selectedProducts.length,
        max: this.maxProducts,
      });
    }

    interpolate(template, values) {
      return Object.entries(values).reduce((result, [key, value]) => {
        return result
          .replaceAll(`{{ ${key} }}`, String(value))
          .replaceAll(`{{${key}}}`, String(value))
          .replaceAll(`[${key}]`, String(value))
          .replaceAll(`{${key}}`, String(value))
          .replaceAll(`%${key}%`, String(value));
      }, String(template || ''));
    }

    getDialog() {
      return this.root.querySelector('.fc-compare-dialog');
    }

    bindDialogEvents() {
      const dialog = this.getDialog();
      if (!dialog || dialog.dataset.finchDialogBound === 'true') return;
      dialog.dataset.finchDialogBound = 'true';
      dialog.addEventListener('close', this.handleDialogClose);
    }

    isDialogOpen(dialog) {
      if (!dialog) return false;
      return dialog.open === true || dialog.hasAttribute('open') || dialog.classList.contains('is-open');
    }

    openDialog(opener) {
      if (this.selectedProducts.length === 0) return;

      const dialog = this.getDialog();
      if (!dialog) return;

      this.dialogOpener = opener;
      this.renderDialogItems();
      dialog.hidden = false;
      dialog.classList.add('is-open');
      dialog.setAttribute('aria-hidden', 'false');

      if (typeof dialog.showModal === 'function') {
        if (!dialog.open) dialog.showModal();
      } else {
        dialog.hidden = false;
        dialog.setAttribute('open', '');
        dialog.setAttribute('role', 'dialog');
        dialog.setAttribute('aria-modal', 'true');
        dialog.querySelector(CLOSE_COMPARE_SELECTOR)?.focus({ preventScroll: true });
      }
    }

    closeDialog() {
      const dialog = this.getDialog();
      if (!dialog || !this.isDialogOpen(dialog)) return;

      if (typeof dialog.close === 'function' && dialog.open) {
        dialog.close();
        return;
      }

      dialog.classList.remove('is-open');
      dialog.removeAttribute('open');
      dialog.hidden = true;
      this.handleDialogClose();
    }

    renderDialogItems() {
      const dialog = this.getDialog();
      const container = dialog?.querySelector('[data-compare-items], .fc-compare-dialog__items');
      if (!dialog || !container) return;

      const fingerprint = JSON.stringify(this.selectedProducts);
      if (fingerprint === this.lastDialogFingerprint && container.children.length > 0) return;

      this.lastDialogFingerprint = fingerprint;
      container.replaceChildren(
        ...this.selectedProducts.map((product) => this.createDialogItem(product, dialog))
      );
    }

    createDialogItem(product, dialog) {
      const template = dialog.querySelector('template[data-compare-item-template]');
      if (template) {
        const fragment = template.content.cloneNode(true);
        const item = fragment.firstElementChild || fragment;
        const image = item.querySelector?.('[data-compare-item-image]');
        const title = item.querySelector?.('[data-compare-item-title]');
        const price = item.querySelector?.('[data-compare-item-price]');
        const type = item.querySelector?.('[data-compare-item-type]');
        const links = item.querySelectorAll?.('[data-compare-item-link]') || [];

        if (image && product.image) {
          image.src = product.image;
          image.alt = product.title;
          image.loading = 'lazy';
        } else if (image) {
          image.hidden = true;
        }
        if (title) title.textContent = product.title;
        if (price) price.textContent = product.price;
        if (type) {
          type.textContent = product.type;
          type.hidden = !product.type;
        }
        links.forEach((link) => {
          link.href = product.url;
          if (!product.url) link.removeAttribute('href');
        });

        return item;
      }

      const item = document.createElement('article');
      item.className = 'fc-compare-dialog__item';
      item.dataset.productId = product.id;

      const link = document.createElement(product.url ? 'a' : 'div');
      link.className = 'fc-compare-dialog__item-link';
      if (product.url) link.href = product.url;

      if (product.image) {
        const image = document.createElement('img');
        image.className = 'fc-compare-dialog__item-image';
        image.src = product.image;
        image.alt = product.title;
        image.loading = 'lazy';
        link.append(image);
      }

      if (product.type) {
        const type = document.createElement('span');
        type.className = 'fc-compare-dialog__item-type';
        type.textContent = product.type;
        link.append(type);
      }

      const title = document.createElement('strong');
      title.className = 'fc-compare-dialog__item-title';
      title.textContent = product.title;
      link.append(title);

      if (product.price) {
        const price = document.createElement('span');
        price.className = 'fc-compare-dialog__item-price';
        price.textContent = product.price;
        link.append(price);
      }

      item.append(link);
      return item;
    }
  }

  function initializeRoot(root) {
    if (!(root instanceof HTMLElement) || registry.controllers.has(root)) return;
    registry.controllers.set(root, new FinchCollectionController(root));
  }

  function syncReadMoreLinks() {
    document.querySelectorAll(READ_MORE_SELECTOR).forEach((link) => {
      const targetSelector = link.getAttribute('href');
      const target = targetSelector?.startsWith('#') ? document.querySelector(targetSelector) : null;
      link.hidden = !target;
    });
  }

  function initializeWithin(scope = document) {
    if (scope instanceof HTMLElement && scope.matches(ROOT_SELECTOR)) initializeRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initializeRoot);
    syncReadMoreLinks();
  }

  function attachDocumentListeners() {
    if (registry.listenersAttached) return;
    registry.listenersAttached = true;

    document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
    document.addEventListener('shopify:section:unload', (event) => {
      const roots = [];
      if (event.target instanceof HTMLElement && event.target.matches(ROOT_SELECTOR)) roots.push(event.target);
      event.target.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
      roots.forEach((root) => registry.controllers.get(root)?.destroy());
      requestAnimationFrame(syncReadMoreLinks);
    });
  }

  attachDocumentListeners();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeWithin(), { once: true });
  } else {
    initializeWithin();
  }
})();
